import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Cake, Award, ChevronLeft, ChevronRight, PartyPopper } from "lucide-react";

const MESECI = [
  "Januar", "Februar", "Marec", "April", "Maj", "Junij",
  "Julij", "Avgust", "September", "Oktober", "November", "December",
];

const fmtDate = (iso) => {
  const [, m, d] = iso.split("-");
  return `${parseInt(d)}. ${parseInt(m)}.`;
};

export default function JubilejiPage() {
  const { token } = useAuth();
  const [leto, setLeto] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/jubileji?leto=${leto}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => alive && setData(res.data))
      .catch(() => alive && toast.error("Napaka pri nalaganju jubilejev"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token, leto]);

  const jubileji = data?.jubileji || [];
  const byMonth = jubileji.reduce((acc, j) => {
    const m = parseInt(j.datum.split("-")[1], 10);
    (acc[m] = acc[m] || []).push(j);
    return acc;
  }, {});

  const stRojstni = jubileji.filter((j) => j.tip === "rojstni_dan").length;
  const stClanstvo = jubileji.filter((j) => j.tip === "clanstvo").length;

  return (
    <div className="space-y-6" data-testid="jubileji-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PartyPopper className="h-6 w-6" />
            Koledar jubilejev
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Okrogli rojstni dnevi in jubileji članstva za lažje načrtovanje priznanj.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setLeto((l) => l - 1)} data-testid="jubileji-prev-year">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-lg font-semibold w-16 text-center" data-testid="jubileji-year">{leto}</span>
          <Button variant="outline" size="icon" onClick={() => setLeto((l) => l + 1)} data-testid="jubileji-next-year">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        <Badge variant="secondary" className="gap-1" data-testid="jubileji-count-birthdays">
          <Cake className="h-3.5 w-3.5" /> {stRojstni} okroglih rojstnih dni
        </Badge>
        <Badge variant="secondary" className="gap-1" data-testid="jubileji-count-membership">
          <Award className="h-3.5 w-3.5" /> {stClanstvo} jubilejev članstva
        </Badge>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Nalagam…</p>
      ) : jubileji.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground" data-testid="jubileji-empty">
            V letu {leto} ni okroglih jubilejev.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {Object.keys(byMonth)
            .map(Number)
            .sort((a, b) => a - b)
            .map((m) => (
              <Card key={m} data-testid={`jubileji-month-${m}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{MESECI[m - 1]}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {byMonth[m].map((j, i) => (
                    <div
                      key={`${j.user_id}-${j.tip}-${i}`}
                      className="flex items-center justify-between gap-2 p-2.5 rounded-md bg-muted/50 border"
                      data-testid={`jubilej-row-${j.user_id}-${j.tip}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {j.tip === "rojstni_dan" ? (
                          <Cake className="h-4 w-4 text-pink-600 shrink-0" />
                        ) : (
                          <Award className="h-4 w-4 text-amber-600 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{j.ime}</div>
                          <div className="text-xs text-muted-foreground">{fmtDate(j.datum)}</div>
                        </div>
                      </div>
                      <Badge variant={j.tip === "rojstni_dan" ? "default" : "outline"} className="shrink-0">
                        {j.tip === "rojstni_dan" ? `${j.jubilej} let` : `${j.jubilej} let članstva`}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
}
